import axios from 'axios'
import {FC, FormEvent, useEffect, useMemo, useState} from 'react'
import {Link, Navigate, useNavigate, useParams} from 'react-router-dom'
import {API_URL} from '../../../../../_metronic/helpers'
import {useAuth} from '../../core/Auth'
import {useRegistrationView} from '../../core/RegisterationProvider'
import {
  canRegisterBusiness,
  canConfirmDuplicateBusiness,
  createIdempotencyKey,
  DuplicateBusinessAction,
  FieldErrors,
  normalizeIdentifier,
  normalizeMobile,
  requiresFreshInquiry,
  taxpayerTypeFromLengths,
  taxpayerTypes,
  TaxpayerType,
  validateBusinessIdentity,
} from './businessRegistration'
import './BusinessRegistration.scss'

interface InquiryResult {
  inquiryId: string
  expiresAtUtc: string
  taxpayerType: TaxpayerType
  taxpayerTypeTitle: string
  taxpayerName: string
  economicCode: string
  nationalIdentifier: string
  fiscalId: string
  taxpayerStatus: string
  fiscalStatus: string
  // Production only; shown, never a reason to refuse the registration (invoices wait until it is active).
  taxFileState?: 'active' | 'notActive' | 'notFound' | 'unavailable' | null
  taxFileStatusTitle?: string | null
  taxFileMessage?: string | null
  operation: 'createBusiness' | 'addFiscalMemory' | 'createDuplicateBusiness'
  existingBusiness?: ExistingBusiness
}

interface ExistingBusiness {
  companyId: number
  name: string
  primaryIdentifier: string
  isActive: boolean
  canAccess: boolean
  redirectUrl?: string
  accessGuidance: string
}

interface ApiErrorBody {
  code?: string
  message?: string
  traceId?: string
  fields?: FieldErrors
  existingBusiness?: ExistingBusiness
}

// The fields take every type's identifiers, so a wrong type never cuts what is typed.
const longestEconomicCode = Math.max(...taxpayerTypes.map((item) => item.economicLength))
const longestNationalIdentifier = Math.max(...taxpayerTypes.map((item) => item.nationalLength))

const userRoles = [
  {value: 0, label: 'مدیر کامل کسب‌وکار'},
  {value: 100, label: 'ویرایش و ارسال صورتحساب'},
  {value: 101, label: 'ایجاد صورتحساب'},
  {value: 102, label: 'فقط مشاهده'},
]

const Registration: FC = () => {
  const {currentUser} = useAuth()
  const {typ} = useParams()
  const navigate = useNavigate()
  const {setHelpPlace} = useRegistrationView()
  const initialType = taxpayerTypes.some((item) => String(item.value) === typ)
    ? (Number(typ) as TaxpayerType)
    : 1

  const [taxpayerType, setTaxpayerType] = useState<TaxpayerType>(initialType)
  const [economicCode, setEconomicCode] = useState('')
  const [nationalIdentifier, setNationalIdentifier] = useState('')
  const [fiscalId, setFiscalId] = useState('')
  const [inquiry, setInquiry] = useState<InquiryResult>()
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState('')
  const [traceId, setTraceId] = useState('')
  const [apiErrorCode, setApiErrorCode] = useState('')
  const [existingBusiness, setExistingBusiness] = useState<ExistingBusiness>()
  const [isInquiring, setIsInquiring] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey)

  const [userRole, setUserRole] = useState(0)
  const [newUserName, setNewUserName] = useState('')
  const [newUserMobile, setNewUserMobile] = useState('')
  const [verificationNow, setVerificationNow] = useState(Date.now)

  const typeOption = useMemo(
    () => taxpayerTypes.find((item) => item.value === taxpayerType)!,
    [taxpayerType]
  )
  // Each identifier pair fits one type only; the form follows it instead of letting a wrong
  // choice turn into «۱۴ رقم» errors the customer cannot place.
  const typeFromIdentifiers = taxpayerTypeFromLengths(economicCode, nationalIdentifier)
  const inquiryExpired = Boolean(
    inquiry && new Date(inquiry.expiresAtUtc).getTime() <= verificationNow
  )
  const normalizedNewUserMobile = normalizeMobile(newUserMobile)
  const addingFiscalMemory = inquiry?.operation === 'addFiscalMemory'
  const creatingDuplicateBusiness = inquiry?.operation === 'createDuplicateBusiness'
  const duplicateConfirmationRequired = canConfirmDuplicateBusiness(
    apiErrorCode,
    existingBusiness?.companyId
  )
  const userIsValid =
    newUserName.trim().length >= 3 &&
    normalizedNewUserMobile.length === 11 &&
    normalizedNewUserMobile.startsWith('09')
  const canSubmit = Boolean(
    inquiry && !inquiryExpired && (addingFiscalMemory || userIsValid) && !isSubmitting
  )

  useEffect(() => {
    if (!inquiry) return
    const expiresAt = new Date(inquiry.expiresAtUtc).getTime()
    const delay = Math.max(0, expiresAt - Date.now())
    const timer = window.setTimeout(() => setVerificationNow(Date.now()), delay + 50)
    return () => window.clearTimeout(timer)
  }, [inquiry])

  useEffect(() => {
    setHelpPlace(
      <div className='business-registration-help'>
        <i className='las la-shield-alt' aria-hidden='true' />
        <h2>ثبت مطمئن کسب‌وکار</h2>
        <p>
          اطلاعات هویتی و حافظه مالیاتی پیش از ثبت با سرویس رسمی تطبیق داده می‌شوند. سایر تنظیمات را
          پس از ایجاد کسب‌وکار تکمیل خواهید کرد.
        </p>
      </div>
    )
  }, [setHelpPlace])

  if (!canRegisterBusiness(currentUser)) {
    return <Navigate to='/dashboard' replace />
  }

  const clearVerification = () => {
    setInquiry(undefined)
    setExistingBusiness(undefined)
    setFormError('')
    setTraceId('')
    setApiErrorCode('')
    setIdempotencyKey(createIdempotencyKey())
  }

  const changeType = (value: TaxpayerType) => {
    setTaxpayerType(value)
    setFieldErrors({})
    clearVerification()
  }

  const changeIdentifiers = (nextEconomicCode: string, nextNationalIdentifier: string) => {
    setEconomicCode(nextEconomicCode)
    setNationalIdentifier(nextNationalIdentifier)
    const detected = taxpayerTypeFromLengths(nextEconomicCode, nextNationalIdentifier)
    if (detected) setTaxpayerType(detected)
    clearVerification()
  }

  const applyApiError = (error: unknown, fallback: string) => {
    const body = axios.isAxiosError(error) ? (error.response?.data as ApiErrorBody) : undefined
    setFieldErrors(body?.fields || {})
    setExistingBusiness(body?.existingBusiness)
    setApiErrorCode(body?.code || '')
    setFormError(body?.code === 'duplicate_confirmation_required' ? '' : body?.message || fallback)
    setTraceId(body?.traceId || '')
  }

  const inquire = async (duplicateAction?: DuplicateBusinessAction, targetCompanyId?: number) => {
    const normalizedEconomicCode = normalizeIdentifier(economicCode)
    const normalizedNationalIdentifier = normalizeIdentifier(nationalIdentifier)
    const normalizedFiscalId = normalizeIdentifier(fiscalId, true)
    const errors = validateBusinessIdentity(
      taxpayerType,
      normalizedEconomicCode,
      normalizedNationalIdentifier,
      normalizedFiscalId
    )
    setFieldErrors(errors)
    setFormError('')
    setExistingBusiness(undefined)
    if (Object.keys(errors).length) return

    setIsInquiring(true)
    try {
      const response = await axios.post(`${API_URL}/taxpayers/registration/inquiry`, {
        taxpayerType,
        economicCode: normalizedEconomicCode,
        nationalIdentifier: normalizedNationalIdentifier,
        fiscalId: normalizedFiscalId,
        duplicateAction,
        targetCompanyId,
      })
      setEconomicCode(normalizedEconomicCode)
      setNationalIdentifier(normalizedNationalIdentifier)
      setFiscalId(normalizedFiscalId)
      const result = response.data.data as InquiryResult
      // The server settles the type from the identifiers; registration must send that one.
      setTaxpayerType(result.taxpayerType)
      setInquiry(result)
      setExistingBusiness(result.existingBusiness)
      setApiErrorCode('')
      setVerificationNow(Date.now())
      setIdempotencyKey(createIdempotencyKey())
      setTraceId(response.data.traceId || '')
    } catch (error) {
      setInquiry(undefined)
      applyApiError(error, 'استعلام انجام نشد. اتصال خود را بررسی و دوباره تلاش کنید.')
    } finally {
      setIsInquiring(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!inquiry || inquiryExpired || (!addingFiscalMemory && !userIsValid) || isSubmitting) {
      if (inquiryExpired) setFormError('اعتبار استعلام پایان یافته است؛ دوباره استعلام بگیرید.')
      return
    }

    const userErrors: FieldErrors = {}
    if (!addingFiscalMemory) {
      if (newUserName.trim().length < 3)
        userErrors['initialUser.fullName'] = 'نام و نام خانوادگی را کامل وارد کنید.'
      if (
        normalizeMobile(newUserMobile).length !== 11 ||
        !normalizeMobile(newUserMobile).startsWith('09')
      )
        userErrors['initialUser.mobile'] = 'شماره موبایل معتبر ۱۱ رقمی وارد کنید.'
    }
    if (Object.keys(userErrors).length) {
      setFieldErrors(userErrors)
      return
    }

    setIsSubmitting(true)
    setFormError('')
    try {
      const response = await axios.post(`${API_URL}/taxpayers/registration`, {
        inquiryId: inquiry.inquiryId,
        idempotencyKey,
        taxpayerType,
        economicCode,
        nationalIdentifier,
        fiscalId,
        initialUser: addingFiscalMemory
          ? undefined
          : {
              fullName: newUserName.trim(),
              mobile: normalizeMobile(newUserMobile),
              role: userRole,
            },
      })
      navigate(response.data.data.redirectUrl, {replace: true})
    } catch (error) {
      const body = axios.isAxiosError(error) ? (error.response?.data as ApiErrorBody) : undefined
      if (requiresFreshInquiry(body?.code)) setInquiry(undefined)
      applyApiError(error, 'ثبت کسب‌وکار انجام نشد. دوباره تلاش کنید.')
      setIsSubmitting(false)
    }
  }

  const fieldError = (name: string) =>
    fieldErrors[name] ? (
      <div className='business-field-error' id={`${name}-error`} role='alert'>
        {fieldErrors[name]}
      </div>
    ) : null

  return (
    <main className='business-registration' dir='rtl'>
      <header className='business-registration__header'>
        <div>
          <span className='business-registration__eyebrow'>مدیریت کسب‌وکارها</span>
          <h1>ثبت کسب‌وکار جدید</h1>
          <p>فقط اطلاعات ضروری را وارد کنید؛ جزئیات تکمیلی بعداً از تنظیمات قابل ثبت است.</p>
        </div>
        <Link to='/apps/taxpayer-management/companies' className='btn btn-light'>
          <i className='las la-arrow-right' aria-hidden='true' /> بازگشت به کسب‌وکارها
        </Link>
      </header>

      <form onSubmit={submit} noValidate>
        <section className='business-section' aria-labelledby='taxpayer-type-title'>
          <div className='business-section__heading'>
            <span>۱</span>
            <div>
              <h2 id='taxpayer-type-title'>نوع مؤدی</h2>
              <p>
                نوع مؤدی را انتخاب کنید؛ با واردکردن شماره اقتصادی و شناسه هویتی، نوع درست خودکار
                انتخاب می‌شود.
              </p>
            </div>
          </div>
          <div
            className='taxpayer-type-grid'
            role='radiogroup'
            aria-label='نوع مؤدی'
            aria-describedby={typeFromIdentifiers ? 'taxpayer-type-detected' : undefined}
          >
            {taxpayerTypes.map((item) => (
              <button
                type='button'
                key={item.value}
                role='radio'
                aria-checked={taxpayerType === item.value}
                className={`taxpayer-type-option ${
                  taxpayerType === item.value ? 'taxpayer-type-option--selected' : ''
                }`}
                onClick={() => changeType(item.value)}
                disabled={
                  isInquiring ||
                  isSubmitting ||
                  (typeFromIdentifiers !== undefined && typeFromIdentifiers !== item.value)
                }
              >
                <i className={`las ${item.icon}`} aria-hidden='true' />
                <strong>{item.title}</strong>
                <small>{item.description}</small>
              </button>
            ))}
          </div>
          {typeFromIdentifiers && (
            <div
              id='taxpayer-type-detected'
              className='business-alert business-alert--info'
              role='status'
              aria-live='polite'
            >
              <i className='las la-magic' aria-hidden='true' />
              <span>
                شماره اقتصادی و {typeOption.nationalLabel} واردشده متعلق به «{typeOption.title}»
                است؛ نوع مؤدی خودکار انتخاب شد.
              </span>
            </div>
          )}
        </section>

        <section className='business-section' aria-labelledby='identity-title'>
          <div className='business-section__heading'>
            <span>۲</span>
            <div>
              <h2 id='identity-title'>اطلاعات هویتی و اقتصادی</h2>
              <p>شناسه‌ها را مطابق پرونده مالیاتی و بدون خط تیره وارد کنید.</p>
            </div>
          </div>
          <div className='business-fields-grid'>
            <div className='business-field'>
              <label htmlFor='economicCode'>شماره اقتصادی</label>
              <input
                id='economicCode'
                dir='ltr'
                inputMode='numeric'
                maxLength={longestEconomicCode}
                value={economicCode}
                disabled={isInquiring || isSubmitting}
                aria-invalid={Boolean(fieldErrors.economicCode)}
                aria-describedby={fieldErrors.economicCode ? 'economicCode-error' : undefined}
                onChange={(event) =>
                  changeIdentifiers(normalizeIdentifier(event.target.value), nationalIdentifier)
                }
              />
              <small>{typeOption.economicLength} رقم</small>
              {fieldError('economicCode')}
            </div>
            <div className='business-field'>
              <label htmlFor='nationalIdentifier'>{typeOption.nationalLabel}</label>
              <input
                id='nationalIdentifier'
                dir='ltr'
                inputMode='numeric'
                maxLength={longestNationalIdentifier}
                value={nationalIdentifier}
                disabled={isInquiring || isSubmitting}
                aria-invalid={Boolean(fieldErrors.nationalIdentifier)}
                aria-describedby={
                  fieldErrors.nationalIdentifier ? 'nationalIdentifier-error' : undefined
                }
                onChange={(event) =>
                  changeIdentifiers(economicCode, normalizeIdentifier(event.target.value))
                }
              />
              <small>{typeOption.nationalLength} رقم</small>
              {fieldError('nationalIdentifier')}
            </div>
          </div>
        </section>

        <section className='business-section' aria-labelledby='fiscal-title'>
          <div className='business-section__heading'>
            <span>۳</span>
            <div>
              <h2 id='fiscal-title'>حافظه مالیاتی و استعلام</h2>
              <p>مالکیت، وضعیت و تطابق شناسه حافظه با مؤدی به‌صورت برخط بررسی می‌شود.</p>
            </div>
          </div>
          <div className='fiscal-inquiry-row'>
            <div className='business-field'>
              <label htmlFor='fiscalId'>شناسه یکتای حافظه مالیاتی</label>
              <input
                id='fiscalId'
                dir='ltr'
                maxLength={6}
                autoCapitalize='characters'
                value={fiscalId}
                disabled={isInquiring || isSubmitting}
                aria-invalid={Boolean(fieldErrors.fiscalId)}
                aria-describedby={fieldErrors.fiscalId ? 'fiscalId-error' : 'fiscalId-help'}
                onChange={(event) => {
                  setFiscalId(normalizeIdentifier(event.target.value, true))
                  clearVerification()
                }}
              />
              <small id='fiscalId-help'>شش کاراکتر درج‌شده در کارپوشه مؤدیان</small>
              {fieldError('fiscalId')}
            </div>
            <button
              type='button'
              className='btn btn-primary business-inquiry-button'
              disabled={isInquiring || isSubmitting}
              onClick={() => inquire()}
            >
              {isInquiring ? (
                <>
                  <span className='spinner-border spinner-border-sm' /> در حال استعلام…
                </>
              ) : (
                <>
                  <i className='las la-search' aria-hidden='true' /> استعلام و تطبیق اطلاعات
                </>
              )}
            </button>
          </div>

          {formError && (
            <div className='business-alert business-alert--error' role='alert'>
              <i className='las la-exclamation-circle' aria-hidden='true' />
              <div>
                <strong>{formError}</strong>
                {traceId && <small>شناسه پیگیری: {traceId}</small>}
              </div>
            </div>
          )}
          {existingBusiness && (
            <div
              className='existing-business'
              role={duplicateConfirmationRequired ? 'alert' : 'status'}
            >
              <div>
                <strong>
                  {duplicateConfirmationRequired
                    ? 'کسب‌وکار مشابه قبلاً ثبت شده است'
                    : creatingDuplicateBusiness
                    ? 'ساخت پنل مجزای تکراری تأیید شد'
                    : addingFiscalMemory
                    ? 'حافظه به کسب‌وکار موجود افزوده می‌شود'
                    : 'این کسب‌وکار قبلاً ثبت شده است'}
                </strong>
                <span>
                  {existingBusiness.name} — {existingBusiness.primaryIdentifier}
                </span>
                <small>
                  {creatingDuplicateBusiness
                    ? 'شناسه‌های هویتی تکراری بررسی شده‌اند و تأیید شما در استعلام ثبت شده است.'
                    : `${existingBusiness.isActive ? 'فعال' : 'غیرفعال'} · ${
                        existingBusiness.accessGuidance
                      }`}
                </small>
              </div>
              <div className='existing-business__actions'>
                {existingBusiness.canAccess && existingBusiness.redirectUrl && (
                  <Link className='btn btn-light-primary' to={existingBusiness.redirectUrl}>
                    بازکردن کسب‌وکار
                  </Link>
                )}
                {duplicateConfirmationRequired && existingBusiness.canAccess && (
                  <button
                    type='button'
                    className='btn btn-light-success'
                    disabled={isInquiring || isSubmitting}
                    onClick={() => inquire('addFiscalMemory', existingBusiness.companyId)}
                  >
                    افزودن حافظه به همین کسب‌وکار
                  </button>
                )}
                {duplicateConfirmationRequired && (
                  <button
                    type='button'
                    className='btn btn-warning'
                    disabled={isInquiring || isSubmitting}
                    onClick={() => inquire('createNew')}
                  >
                    تأیید و ساخت پنل مجزای جدید
                  </button>
                )}
              </div>
            </div>
          )}
          {inquiry && !inquiryExpired && (
            <div className='verified-business' role='status' aria-live='polite'>
              <div className='verified-business__title'>
                <i className='las la-check-circle' aria-hidden='true' />
                <div>
                  <strong>اطلاعات با موفقیت تأیید شد</strong>
                  <small>این نتیجه تا ۱۰ دقیقه برای ثبت معتبر است.</small>
                </div>
              </div>
              <dl>
                <div>
                  <dt>نام مؤدی</dt>
                  <dd>{inquiry.taxpayerName}</dd>
                </div>
                <div>
                  <dt>نوع مؤدی</dt>
                  <dd>{inquiry.taxpayerTypeTitle}</dd>
                </div>
                <div>
                  <dt>شماره اقتصادی</dt>
                  <dd dir='ltr'>{inquiry.economicCode}</dd>
                </div>
                <div>
                  <dt>{typeOption.nationalLabel}</dt>
                  <dd dir='ltr'>{inquiry.nationalIdentifier}</dd>
                </div>
                <div>
                  <dt>حافظه مالیاتی</dt>
                  <dd dir='ltr'>{inquiry.fiscalId}</dd>
                </div>
                <div>
                  <dt>وضعیت</dt>
                  <dd className='text-success'>فعال</dd>
                </div>
                {inquiry.taxFileState && (
                  <div>
                    <dt>وضعیت کارپوشه</dt>
                    <dd
                      className={
                        inquiry.taxFileState === 'active'
                          ? 'text-success'
                          : inquiry.taxFileState === 'unavailable'
                          ? 'text-warning'
                          : 'text-danger'
                      }
                    >
                      {inquiry.taxFileStatusTitle || 'نامشخص'}
                    </dd>
                  </div>
                )}
              </dl>
              {inquiry.taxFileState && inquiry.taxFileState !== 'active' && inquiry.taxFileMessage && (
                <div className='business-alert business-alert--warning mt-3' role='note'>
                  {inquiry.taxFileMessage}
                </div>
              )}
            </div>
          )}
          {inquiryExpired && (
            <div className='business-alert business-alert--warning' role='alert'>
              اعتبار استعلام پایان یافته است. برای ادامه دوباره استعلام بگیرید.
            </div>
          )}
        </section>

        {!addingFiscalMemory && (
          <section className='business-section' aria-labelledby='user-title'>
            <div className='business-section__heading'>
              <span>۴</span>
              <div>
                <h2 id='user-title'>کاربر اولیه کسب‌وکار</h2>
                <p>نام و شماره موبایل کاربر را وارد کنید؛ همین دو مورد کافی است.</p>
              </div>
            </div>
            <div className='business-fields-grid'>
              <div className='business-field'>
                <label htmlFor='newUserName'>نام و نام خانوادگی</label>
                <input
                  id='newUserName'
                  value={newUserName}
                  maxLength={100}
                  onChange={(event) => setNewUserName(event.target.value)}
                />
                {fieldError('initialUser.fullName')}
              </div>
              <div className='business-field'>
                <label htmlFor='newUserMobile'>شماره موبایل</label>
                <input
                  id='newUserMobile'
                  dir='ltr'
                  inputMode='tel'
                  maxLength={11}
                  value={newUserMobile}
                  onChange={(event) => setNewUserMobile(normalizeMobile(event.target.value))}
                />
                {fieldError('initialUser.mobile')}
              </div>
              <p className='new-user-note'>
                <i className='las la-info-circle' /> اگر این شماره موبایل قبلاً ثبت شده باشد، همان
                کاربر موجود به این کسب‌وکار افزوده می‌شود و کاربر تکراری ساخته نخواهد شد.
              </p>
            </div>
            <div className='business-field business-field--role'>
              <label htmlFor='userRole'>سطح دسترسی کاربر در کسب‌وکار</label>
              <select
                id='userRole'
                value={userRole}
                onChange={(event) => setUserRole(Number(event.target.value))}
              >
                {userRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {fieldError('initialUser.role')}
            </div>
          </section>
        )}
        <section className='business-summary' aria-labelledby='summary-title'>
          <div>
            <h2 id='summary-title'>خلاصه ثبت</h2>
            <p>
              {inquiry
                ? 'اطلاعات تأییدشده آماده ثبت است.'
                : 'پس از استعلام موفق، خلاصه نهایی اینجا تکمیل می‌شود.'}
            </p>
          </div>
          <ul>
            <li>
              <span>کسب‌وکار</span>
              <strong>{inquiry?.taxpayerName || '—'}</strong>
            </li>
            <li>
              <span>نوع مؤدی</span>
              <strong>{typeOption.title}</strong>
            </li>
            <li>
              <span>{addingFiscalMemory ? 'عملیات' : 'کاربر اولیه'}</span>
              <strong>
                {addingFiscalMemory
                  ? 'افزودن حافظه به کسب‌وکار موجود'
                  : newUserName || 'تکمیل نشده'}
              </strong>
            </li>
            {creatingDuplicateBusiness && (
              <li>
                <span>نحوه ثبت</span>
                <strong>پنل مجزا با شناسه‌های هویتی تکراری</strong>
              </li>
            )}
            {!addingFiscalMemory && (
              <li>
                <span>سطح دسترسی</span>
                <strong>{userRoles.find((role) => role.value === userRole)?.label}</strong>
              </li>
            )}
          </ul>
        </section>

        <footer className='business-registration__actions'>
          <div>
            <i className='las la-lock' aria-hidden='true' />
            <span>
              {addingFiscalMemory
                ? 'حافظه جدید بدون تغییر اطلاعات کسب‌وکار موجود افزوده می‌شود.'
                : creatingDuplicateBusiness
                ? 'پنل مجزای جدید با تأیید شما ساخته می‌شود؛ شناسه حافظه مالیاتی همچنان یکتا است.'
                : 'ثبت کسب‌وکار، حافظه مالیاتی و کاربر اولیه به‌صورت یکپارچه انجام می‌شود.'}
            </span>
          </div>
          <button type='submit' className='btn btn-success btn-lg' disabled={!canSubmit}>
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm' /> در حال ثبت…
              </>
            ) : (
              <>
                <i className='las la-check' aria-hidden='true' />{' '}
                {addingFiscalMemory
                  ? 'افزودن حافظه مالیاتی'
                  : creatingDuplicateBusiness
                  ? 'ثبت پنل مجزای جدید'
                  : 'ثبت کسب‌وکار'}
              </>
            )}
          </button>
        </footer>
      </form>
    </main>
  )
}

export {Registration}
